import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../../../components/DataGrid/DataGrid';
import { autoMobileVehicleService } from '../../../../services/autoMobileVehicleService';
import { CirclePlus, RefreshCw, SquarePen, Trash2, Eye, Car, CheckCircle2, FileText } from 'lucide-react';
import '../AutoMobilePage.css';

const VehicleListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unwrap = (res: any): any[] => {
    const d = res?.data?.data;
    return Array.isArray(d) ? d : (d?.data ?? []);
  };

  const loadItems = useCallback(async () => {
    setLoading(true); setError(null);
    try { setItems(unwrap(await autoMobileVehicleService.getAll())); }
    catch { setError(t('automobile.vehicle.failedToLoad')); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try { setItems(unwrap(await autoMobileVehicleService.getAll())); }
    catch { setError(t('automobile.vehicle.failedToLoad')); }
    finally { setRefreshing(false); }
  };

  const handleDelete = async (row: any) => {
    if (!window.confirm(t('automobile.vehicle.deleteConfirm'))) return;
    try {
      await autoMobileVehicleService.delete(row.id);
      setItems((prev) => prev.filter((i) => i.id !== row.id));
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? t('common.error'));
    }
  };

  const columns: GridColumn[] = [
    { key: 'registrationNumber', label: t('automobile.vehicle.registrationNumber'), sortable: true, width: '140px',
      render: (v) => <strong>{v}</strong> },
    { key: 'customerName', label: t('automobile.vehicle.customer'), sortable: true, width: '180px' },
    { key: 'make',  label: t('automobile.vehicle.make'),  sortable: true, width: '130px' },
    { key: 'model', label: t('automobile.vehicle.model'), sortable: true, width: '130px' },
    { key: 'year',  label: t('automobile.vehicle.year'),  sortable: true, width: '90px' },
    { key: 'mileage', label: t('automobile.vehicle.mileage'), width: '110px',
      render: (v) => <span>{v?.toLocaleString?.() ?? v}</span> },
    { key: 'invoiceCount', label: t('automobile.vehicle.invoiceCount'), width: '110px' },
    { key: 'isActive', label: t('common.status'), width: '100px',
      render: (v) => <span className={`ims-badge ${v ? 'auto-badge-active' : 'auto-badge-inactive'}`}>{v ? t('common.active') : t('common.inactive')}</span> },
  ];

  const actions: GridAction[] = [
    { icon: <Eye size={16} />, onClick: (r) => navigate(`/automobile/vehicles/${r.id}`),
      className: 'grid-action-btn-view', title: t('common.view') },
    { icon: <SquarePen size={16} />, onClick: (r) => navigate(`/automobile/vehicles/${r.id}/edit`),
      className: 'grid-action-btn-edit', title: t('common.edit') },
    { icon: <Trash2 size={16} />, onClick: handleDelete,
      className: 'grid-action-btn-delete', title: t('common.delete') },
  ];

  const activeCount = items.filter((i) => i.isActive).length;
  const totalInvoices = items.reduce((s, i) => s + (i.invoiceCount ?? 0), 0);

  return (
    <Layout>
      <div className="ims-page auto-page">
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">{t('automobile.vehicle.title')}</h1>
            <p className="ims-list-subtitle">{t('automobile.vehicle.subtitle')}</p>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw size={15} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
            <button className="btn-new" onClick={() => navigate('/automobile/vehicles/create')}>
              <CirclePlus size={16} /> {t('automobile.vehicle.newVehicle')}
            </button>
          </div>
        </div>

        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon auto-icon-teal"><Car size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{items.length}</span>
              <span className="ims-stat-label">{t('automobile.vehicle.total')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon auto-icon-green"><CheckCircle2 size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{activeCount}</span>
              <span className="ims-stat-label">{t('common.active')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon auto-icon-blue"><FileText size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{totalInvoices}</span>
              <span className="ims-stat-label">{t('automobile.invoice.title')}</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="ims-alert ims-alert-error">
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="ims-grid-wrapper">
          <DataGrid data={items} columns={columns} actions={actions} loading={loading}
            pageSize={10} searchPlaceholder={t('common.search')}
            emptyMessage={t('automobile.vehicle.noVehiclesFound')}
            rowsPerPageOptions={[5, 10, 25, 50]} />
        </div>
      </div>
    </Layout>
  );
};

export default VehicleListPage;
